import { ChangeEvent, useEffect, useState } from 'react';

import { Cancel, CheckCircle, Refresh } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, TextField, Typography, styled } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ApplicationStatus } from '@prisma/client';

import BorderedCard from '../BorderedCard/BorderedCard';
import { Flex } from '../Flex';
import { showSuccessToast } from '../Toasts/toast';

const StyledTextField = styled(TextField)(() => {
    return {
        '& .MuiOutlinedInput-root': {
            '& input': {
                padding: '10.5px 14px',
            },
        },
    };
});

interface ManageSignatureProps {
    signer: { firstName: string; lastName: string; email: string };
    isSigned: boolean;
    mid: string;
    serviceApplicationStatus: ApplicationStatus;
    setTerminalId: (value: string) => void;
    onResendAgreement: () => void;
    resendingLoading: boolean;
    isResendDisabled: boolean;
    handleRefresh: () => void;
    isRefreshing: boolean;
    isOnlineServiceType: boolean;
    tid?: string;
}

const ManageSignature = ({
    signer,
    isSigned,
    mid,
    serviceApplicationStatus,
    setTerminalId,
    onResendAgreement,
    resendingLoading,
    isResendDisabled,
    handleRefresh,
    isRefreshing,
    isOnlineServiceType,
    tid,
}: ManageSignatureProps) => {
    const theme = useTheme();
    const [values, setValues] = useState({ terminalId: tid || '' });
    const [errors, setErrors] = useState({ terminalId: false });

    // Update local state when tid prop changes
    useEffect(() => {
        setValues((prevValues) => {
            return { ...prevValues, terminalId: tid || '' };
        });
    }, [tid]);

    // Update parent component with terminal ID value
    useEffect(() => {
        setTerminalId(values.terminalId);
    }, [values.terminalId, setTerminalId]);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setValues({ ...values, [name]: value });
        setErrors({ ...errors, [name]: value === '' });
    };

    const handleTIDBlur = () => {
        setErrors({ terminalId: values.terminalId === '' });
    };

    const isServiceOrderEditable =
        serviceApplicationStatus !== ApplicationStatus.AWAITING_HARDWARE_DISPATCH &&
        serviceApplicationStatus !== ApplicationStatus.ACTIVE;

    const showServiceOrderSection =
        serviceApplicationStatus !== ApplicationStatus.DRAFT &&
        serviceApplicationStatus !== ApplicationStatus.AWAITING_SIGNATURE &&
        serviceApplicationStatus !== ApplicationStatus.ERROR_IDENTIFIED;

    return (
        <Flex
            width="100%"
            gap={{ xs: '12px', sm: '24px' }}
            flexDirection={{ xs: 'column', sm: 'row' }}
            flexWrap="wrap"
        >
            <BorderedCard flexDirection="column">
                <Typography
                    fontWeight="500"
                    fontSize="22px"
                    lineHeight="34px"
                    color={theme.palette.text.primary}
                    marginRight="16px"
                >
                    Manage Signature
                </Typography>
                <Flex flexDirection="column" gap="16px">
                    <Typography
                        fontWeight="500"
                        fontSize="14px"
                        lineHeight="20px"
                        color={theme.palette.text.primary}
                        marginTop="20px"
                        marginBottom={signer ? '10px' : '0px'}
                    >
                        Signer(s)
                    </Typography>
                    <Flex flexDirection={{ xs: 'column', sm: 'row' }}>
                        <Flex
                            flexDirection={{ xs: 'column', sm: 'row' }}
                            sx={{
                                marginTop: { xs: '0', sm: '8px' },
                            }}
                        >
                            <Typography
                                fontWeight="500"
                                fontSize="14px"
                                lineHeight="20px"
                                color="text.primary"
                                sx={{
                                    mr: { xs: 0, sm: '16px' },
                                    mb: { xs: '8px', sm: 0 },
                                }}
                            >
                                {`${signer?.firstName} ${signer?.lastName}`}
                            </Typography>
                            <Typography
                                fontWeight="500"
                                fontSize="14px"
                                lineHeight="20px"
                                color="text.primary"
                            >
                                {signer?.email}
                            </Typography>
                        </Flex>
                        <Flex
                            alignItems="center"
                            gap="4px"
                            sx={{
                                ml: { xs: 0, sm: '16px' },
                                mt: { xs: '8px', sm: 0 },
                            }}
                        >
                            {isSigned ? (
                                <CheckCircle sx={{ color: theme.palette.orange.main }} />
                            ) : (
                                <>
                                    <Cancel sx={{ color: theme.palette.orange.main }} />
                                    <LoadingButton
                                        onClick={() => {
                                            handleRefresh();
                                            showSuccessToast('Agreement status has been refreshed.');
                                        }}
                                        loading={isRefreshing}
                                        loadingPosition="start"
                                        startIcon={<Refresh />}
                                        sx={{
                                            color: isRefreshing
                                                ? theme.palette.grey.A400
                                                : theme.palette.orange.main,
                                            minWidth: 'auto',
                                            p: '2px',
                                            '& .MuiSvgIcon-root': { fontSize: '1.5rem' },
                                        }}
                                    />
                                    <LoadingButton
                                        variant="npi-primary"
                                        onClick={onResendAgreement}
                                        loading={resendingLoading}
                                        disabled={isResendDisabled}
                                        sx={{ minWidth: 'auto', p: '6px 10px' }}
                                    >
                                        Resend
                                    </LoadingButton>
                                </>
                            )}
                        </Flex>
                    </Flex>
                </Flex>
            </BorderedCard>

            {showServiceOrderSection ? (
                <BorderedCard flex={1}>
                    <Typography
                        fontWeight="500"
                        fontSize="24px"
                        lineHeight="34px"
                        color="text.primary"
                        marginBottom="10px"
                    >
                        Complete Service Order (Add MID)
                    </Typography>

                    <Box display="flex" flexDirection="column" gap={2}>
                        <Typography>Merchant ID (MID)</Typography>
                        <StyledTextField variant="outlined" value={mid} disabled />

                        {!isOnlineServiceType && (
                            <>
                                <Typography>Terminal ID (TID)</Typography>
                                <StyledTextField
                                    placeholder="Enter Terminal ID (TID)"
                                    name="terminalId"
                                    variant="outlined"
                                    value={values.terminalId}
                                    onChange={handleChange}
                                    onBlur={handleTIDBlur}
                                    error={errors.terminalId}
                                    helperText={errors.terminalId ? 'Terminal ID is required' : ''}
                                    disabled={!isServiceOrderEditable}
                                />
                            </>
                        )}
                    </Box>
                </BorderedCard>
            ) : (
                <Box flex={1} />
            )}
        </Flex>
    );
};

export default ManageSignature;
